import { PencilSimple, Trash } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Tijdbalk, TijdbalkTicks } from "@/components/Tijdbalk";

const nl = (n) => String(n).replace(".", ",");

const tijd = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const pauzeTekst = (min) => {
  if (!min) return "–";
  return min >= 60 ? `${Math.floor(min / 60)}u${String(min % 60).padStart(2, "0")}` : `${min}m`;
};

const KOLOMMEN = "grid-cols-[minmax(140px,1.4fr)_64px_64px_76px_64px_minmax(110px,1fr)_minmax(180px,2fr)_136px_84px]";
const CEL = "px-2 border-l border-border first:border-l-0 min-w-0";

export const AanwezigheidFiliaalTabel = ({ filiaal, pauzes = {}, onEdit, onDelete }) => {
  const records = filiaal.records;
  return (
    <div data-testid={`filiaal-tabel-${filiaal.naam}`}>
      {/* ── Desktop: kolommen met rasterlijnen + tijdbalk ── */}
      <div className="hidden lg:block">
        <div className={`grid ${KOLOMMEN} bg-muted/30 border-t border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground`}>
          <span className={`${CEL} py-1.5`}>Medewerker</span>
          <span className={`${CEL} py-1.5 text-center`}>In</span>
          <span className={`${CEL} py-1.5 text-center`}>Uit</span>
          <span className={`${CEL} py-1.5 text-center`}>Duur</span>
          <span className={`${CEL} py-1.5 text-center`}>Pauze</span>
          <span className={`${CEL} py-1.5`}>Filiaal</span>
          <span className={`${CEL} py-1.5 flex items-center`}><TijdbalkTicks className="w-full" /></span>
          <span className={`${CEL} py-1.5 text-center`}>Status</span>
          <span className={`${CEL} py-1.5 text-right`}>Acties</span>
        </div>

        {records.map((r) => (
          <div
            key={r.id}
            className={`grid ${KOLOMMEN} border-t border-border items-center hover:bg-[#EAF1FF]/40 transition-colors`}
            data-testid={`aanwezigheid-rij-${r.id}`}
          >
            <span className={`${CEL} py-2 text-sm font-semibold truncate`}>{r.medewerker_naam}</span>
            <span className={`${CEL} py-2 text-center`}>
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="text-xs font-mono text-[#002FA7] underline decoration-dotted underline-offset-2"
                title="Tik om de tijden aan te passen"
                data-testid={`tijd-in-${r.id}`}
              >
                {r.handmatig ? "–" : tijd(r.inklok_tijd) || "--:--"}
              </button>
            </span>
            <span className={`${CEL} py-2 text-center`}>
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="text-xs font-mono text-[#002FA7] underline decoration-dotted underline-offset-2"
                data-testid={`tijd-uit-${r.id}`}
              >
                {r.handmatig ? "–" : tijd(r.uitklok_tijd) || "--:--"}
              </button>
            </span>
            <span className={`${CEL} py-2 text-sm font-black tabular-nums text-center`}>
              {r.uren ? `${nl(r.uren)} u` : !r.uitklok_tijd && !r.handmatig ? "lopend" : "–"}
            </span>
            <span className={`${CEL} py-2 text-xs tabular-nums text-center text-muted-foreground`}>
              {pauzeTekst(pauzes[r.id])}
            </span>
            <span className={`${CEL} py-2 text-xs text-muted-foreground truncate`}>
              {r.filiaal_naam || filiaal.naam}
            </span>
            <span className={`${CEL} py-2`}>
              {r.handmatig
                ? <span className="text-[10px] text-muted-foreground">handmatig ingevoerd</span>
                : <Tijdbalk startIso={r.inklok_tijd} eindIso={r.uitklok_tijd} />}
            </span>
            <span className={`${CEL} py-2 flex justify-center gap-1 whitespace-nowrap`}>
              {r.te_laat && (
                <Badge variant="outline" className="text-[10px] whitespace-nowrap bg-[#E53E3E]/10 text-[#E53E3E] border-[#E53E3E]/30">Te laat</Badge>
              )}
              {r.handmatig ? (
                <Badge variant="outline" className="text-[10px] whitespace-nowrap bg-[#002FA7]/5 text-[#002FA7] border-[#002FA7]/20">Handmatig</Badge>
              ) : !r.uitklok_tijd ? (
                <Badge variant="outline" className="text-[10px] whitespace-nowrap bg-[#38A169]/10 text-[#38A169] border-[#38A169]/30">Actief</Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px] whitespace-nowrap">Voltooid</Badge>
              )}
            </span>
            <span className={`${CEL} py-2 flex justify-end gap-1.5`}>
              <button onClick={() => onEdit(r)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border" data-testid={`edit-record-${r.id}`}>
                <PencilSimple size={14} />
              </button>
              <button onClick={() => onDelete(r.id)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border text-destructive" data-testid={`delete-record-${r.id}`}>
                <Trash size={14} />
              </button>
            </span>
          </div>
        ))}
      </div>

      {/* ── Mobiel / tablet: compacte kaart ── */}
      <div className="lg:hidden">
        {records.map((r) => (
          <div key={r.id} className="px-3 py-2 border-t border-border flex items-start gap-2" data-testid={`aanwezigheid-kaart-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{r.medewerker_naam}</p>
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="text-[11px] font-mono text-[#002FA7] underline decoration-dotted underline-offset-2 text-left"
                data-testid={`tijden-${r.id}`}
              >
                {`${tijd(r.inklok_tijd) || "--:--"} → ${tijd(r.uitklok_tijd) || "--:--"}`}
                {pauzes[r.id] ? ` · pauze ${pauzeTekst(pauzes[r.id])}` : ""}
                {r.handmatig ? " (handmatig)" : ""}
              </button>
              {!r.handmatig && (
                <div className="mt-1.5 max-w-[220px]">
                  <Tijdbalk startIso={r.inklok_tijd} eindIso={r.uitklok_tijd} />
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-1">
                {r.handmatig && <Badge variant="outline" className="text-[10px] bg-[#002FA7]/5 text-[#002FA7] border-[#002FA7]/20">Handmatig</Badge>}
                {r.te_laat && <Badge variant="outline" className="text-[10px] bg-[#E53E3E]/10 text-[#E53E3E] border-[#E53E3E]/30">Te laat</Badge>}
                {!r.uitklok_tijd && !r.handmatig
                  ? <Badge variant="outline" className="text-[10px] bg-[#38A169]/10 text-[#38A169] border-[#38A169]/30">Actief</Badge>
                  : !r.handmatig ? <Badge variant="secondary" className="text-[10px]">Voltooid</Badge> : null}
              </div>
            </div>
            <span className="text-sm font-black tabular-nums shrink-0 mt-0.5">
              {r.uren ? `${nl(r.uren)} u` : "–"}
            </span>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => onEdit(r)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border" data-testid={`edit-record-m-${r.id}`}>
                <PencilSimple size={14} />
              </button>
              <button onClick={() => onDelete(r.id)} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border text-destructive" data-testid={`delete-record-m-${r.id}`}>
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Subtotaal per filiaal ── */}
      <div
        className="flex items-center gap-3 px-3 py-2 border-t border-border bg-[#EAF1FF]/60 text-[11px] font-bold"
        data-testid={`filiaal-totaal-${filiaal.naam}`}
      >
        <span className="uppercase tracking-wider text-[#002FA7]">Subtotaal {filiaal.naam}</span>
        <span className="text-muted-foreground font-semibold">
          {filiaal.medewerkers} medewerker{filiaal.medewerkers === 1 ? "" : "s"} · {records.length} registratie{records.length === 1 ? "" : "s"}
          {filiaal.teLaat > 0 ? ` · ${filiaal.teLaat}x te laat` : ""}
        </span>
        <span className="ml-auto tabular-nums text-sm font-black">{nl(filiaal.uren.toFixed(2))} u</span>
      </div>
    </div>
  );
};
